import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import type { Resposta } from './tipos';

/** M3.3: respostas rápidas acionadas por /atalho no campo de mensagem. */
export function RespostasRapidas() {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Resposta | 'nova' | null>(null);
  const [remover, setRemover] = useState(false);
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  // Val5 (29/10): respostas rápidas são configuração do atendimento (`conversas.configurar`).
  const podeEditar = s.pode('conversas.configurar');

  const atualizar = () => qc.invalidateQueries({ queryKey: ['conversas', 'respostas'] }).then(() => qc.invalidateQueries({ queryKey: ['/conversas/respostas-rapidas'] }));
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/conversas/respostas-rapidas/${id}`),
    onSuccess: async () => {
      await atualizar();
      toast.sucesso(tn('comum.removido'));
      setRemover(false);
      setAberto(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    { nome: 'atalho', rotulo: t('respostas.atalho'), tipo: 'texto', obrigatorio: true, ajuda: t('respostas.ajudaAtalho'), desabilitado: !podeEditar },
    { nome: 'texto', rotulo: t('respostas.texto'), tipo: 'area', obrigatorio: true, desabilitado: !podeEditar },
  ];
  const colunas: Coluna<Resposta>[] = [
    { chave: 'atalho', rotulo: t('respostas.atalho'), principal: true, mono: true, render: (r) => <span className="text-acento-forte">/{r.atalho}</span> },
    { chave: 'texto', rotulo: t('respostas.texto'), render: (r) => <span className="line-clamp-2 whitespace-pre-wrap" title={r.texto}>{r.texto}</span> },
  ];

  return (
    <>
      {(podeEditar || s.pode('conversas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('conversas.excluir') && <BotaoLixeira entidades={['conversas.resposta_rapida']} />}
          {podeEditar && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
              {t('acoes.novaResposta')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Resposta> tela="conversas.respostas" caminho="/conversas/respostas-rapidas" colunas={colunas} aoClicar={(r) => setAberto(r)} vazio={<Vazio texto={t('respostas.vazio')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? `/${atual.atalho}` : t('acoes.novaResposta')}
        rodape={
          <>
            {atual && s.pode('conversas.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {podeEditar && (
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-resposta">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-resposta"
          campos={campos}
          inicial={(atual ?? {}) as Record<string, unknown>}
          aoEnviar={async (v) => {
            const corpo = { atalho: String(v.atalho ?? '').trim(), texto: String(v.texto ?? '') };
            if (atual) await patch(`/conversas/respostas-rapidas/${atual.id}`, corpo);
            else await post('/conversas/respostas-rapidas', corpo);
            await atualizar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => atual && excluir.mutate(atual.id)} carregando={excluir.isPending} titulo={t('respostas.removerTitulo')} texto={t('respostas.removerTexto')} perigoso />
    </>
  );
}
